import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Heart, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import ProductCard from '@/components/ProductCard';
import { useWishlist } from '@/context/WishlistContext';
import { useSeo } from '@/hooks/useSeo';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';

export default function WishlistPage() {
  useSeo({
    title: 'I Tuoi Preferiti',
    description: 'I gioielli che hai salvato tra i preferiti nella collezione Ciro Gallotti.',
    path: '/preferiti',
    noindex: true,
  });

  const { wishlist } = useWishlist();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await axios.get(`${API}/products`);
        setProducts(res.data || []);
      } catch (e) {
        console.error('Error fetching products:', e);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const savedProducts = products.filter((p) => wishlist.includes(p.id));

  return (
    <div className="bg-ivory pt-24 md:pt-28 min-h-screen" data-testid="wishlist-page">
      <header className="px-6 md:px-12 lg:px-16 py-16 md:py-20 hairline-b">
        <div className="max-w-container mx-auto">
          <div className="flex items-center gap-4 mb-5">
            <div className="w-12 h-px bg-bordeaux" />
            <p className="text-eyebrow text-bordeaux">La Tua Selezione</p>
          </div>
          <h1 className="font-display text-h1 text-ink">
            I Tuoi <span className="italic text-ink/70">Preferiti</span>
          </h1>
          <p className="mt-6 max-w-2xl text-ink/65 leading-relaxed">
            {savedProducts.length > 0
              ? `${savedProducts.length} ${savedProducts.length === 1 ? 'gioiello salvato' : 'gioielli salvati'}. I preferiti restano salvati su questo dispositivo.`
              : 'Salva i gioielli che ami per ritrovarli con un tocco.'}
          </p>
        </div>
      </header>

      <div className="max-w-container mx-auto px-6 md:px-12 lg:px-16 py-12 md:py-16">
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8">
            {[1, 2, 3].map((i) => (
              <div key={i} className="animate-pulse">
                <div className="aspect-[3/4] bg-line rounded-2xl mb-5" />
                <div className="h-3 bg-line w-20 mb-3" />
                <div className="h-5 bg-line w-40" />
              </div>
            ))}
          </div>
        ) : savedProducts.length === 0 ? (
          <div className="text-center py-24" data-testid="wishlist-empty">
            <div className="w-20 h-20 mx-auto mb-8 rounded-full bg-bordeaux/10 flex items-center justify-center">
              <Heart className="w-8 h-8 text-bordeaux" strokeWidth={1.5} />
            </div>
            <h2 className="font-display text-h3 text-ink mb-4">Nessun preferito ancora</h2>
            <p className="text-muted max-w-md mx-auto mb-8">
              Esplora la collezione e tocca il cuore sui gioielli che vuoi tenere d'occhio.
            </p>
            <Link to="/collezione">
              <Button variant="accent" size="lg" data-testid="wishlist-browse-btn">
                Esplora la Collezione
                <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-x-8 gap-y-12">
            {savedProducts.map((product) => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
